// Pure event-log state management (Phase 7 Block F extraction).
//
// No DOM dependency — importable by server-side vitest. The DOM layer
// (event-log.ts) wraps this class and supplies a real scheduleFade
// implementation backed by setTimeout.

export const FADE_AFTER_MS = 8000; // contracts:allow-literal — 8 s visible
export const FADE_DURATION_MS = 1000; // contracts:allow-literal — 1 s CSS fade
export const MAX_ENTRIES = 80; // contracts:allow-literal — cap for DOM size

export type EventKind =
  | "gather"
  | "craft"
  | "xp"
  | "xpTier"
  | "market"
  | "party"
  | "expedition"
  | "repair"
  | "error";

export type LogEntry = {
  readonly id: number;
  readonly text: string;
  readonly kind: EventKind;
  readonly spawnedAt: number;
};

// Injectable: called when an entry should start fading after delayMs.
// DOM layer uses setTimeout; tests use a mock recorder.
export type ScheduleFadeFn = (entry: LogEntry, delayMs: number) => void;

export class EventLogState {
  private _entries: LogEntry[] = [];
  private _hovered = false;
  private _nextId = 0;
  private readonly _scheduleFade: ScheduleFadeFn;

  constructor(scheduleFade: ScheduleFadeFn) {
    this._scheduleFade = scheduleFade;
  }

  push(text: string, kind: EventKind, now = Date.now()): number | null {
    let evictedId: number | null = null;
    if (this._entries.length >= MAX_ENTRIES) {
      const oldest = this._entries.shift();
      if (oldest !== undefined) evictedId = oldest.id;
    }
    const entry: LogEntry = { id: this._nextId++, text, kind, spawnedAt: now };
    this._entries.push(entry);
    if (!this._hovered) {
      this._scheduleFade(entry, FADE_AFTER_MS);
    }
    return evictedId;
  }

  setHovered(hovered: boolean, now = Date.now()): void {
    this._hovered = hovered;
    if (!hovered) {
      // Restart fades for entries that elapsed while the panel was hovered.
      for (const entry of this._entries) {
        if (now - entry.spawnedAt >= FADE_AFTER_MS) {
          this._scheduleFade(entry, 0);
        }
      }
    }
  }

  removeEntry(id: number): void {
    this._entries = this._entries.filter((e) => e.id !== id);
  }

  get count(): number {
    return this._entries.length;
  }

  get hovered(): boolean {
    return this._hovered;
  }

  getEntries(): ReadonlyArray<LogEntry> {
    return this._entries;
  }
}
